/* eslint-disable @typescript-eslint/no-explicit-any */
import { IDictionary } from '@common/interfaces/dictionary.interface';
import dayjs from 'dayjs';
import React, { ChangeEventHandler, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    DeepPartial,
    Path,
    RegisterOptions,
    SubmitHandler,
    UnpackNestedValue,
    useForm as useReactHookForm,
    UseFormProps,
    UseFormReturn,
} from 'react-hook-form';

export type RegisterOptionsEx = RegisterOptions;
export declare type FieldChangeHandler<TFieldValues> = (name: keyof TFieldValues, data: TFieldValues) => void;
export declare type FieldBlurHandler<TFieldValues> = (name: keyof TFieldValues, data: TFieldValues) => void;
interface ICustomFormProps<T> {
    enableReinitialize: boolean;
    isDirty?: boolean;
    onSubmit: SubmitHandler<T>;
    onFieldBlur?: FieldBlurHandler<T>;
    onFieldChange?: FieldChangeHandler<T>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IUseCustomFormReturn<T = any> {
    submit: () => void;
    formContext: UseFormReturn<T>;
    isValid: boolean;
    setBlur: (name: keyof T) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue: (name: keyof T, value: any) => void;
    setBlurForAllFields: () => void;
    props: Pick<ICustomFormProps<T>, 'onFieldBlur' | 'onFieldChange' | 'onSubmit'>;
    _onBlurHandlers: IDictionary<ChangeEventHandler>;
    defaultValues?: UnpackNestedValue<DeepPartial<T>>;
}

interface IProps<T> extends UseFormProps<T>, ICustomFormProps<T> {}

// 1 validate first time when no any data (in formik in the box)
// 2 enableReinitialize -> feature of reinitialize when default properties changes (in formik in the box)
// 3 manually trigger onBlur event for validation
// 4 isDirty feature - when form already have populated data - trigger validate for those fields.
// 5 added onFieldBlur and onFieldChange handlers (need context for those fields)
// note: register by 'ref' not possible with antd - written custom useField hook (or investigate more).
// antd form: (rc-form-field) - don't have feature isValid flag.
export const useForm = <TValues = any>(props: IProps<TValues>): IUseCustomFormReturn<TValues> => {
    const _onBlurHandlersRef = useRef<IDictionary<ChangeEventHandler>>({});

    const { onSubmit, onFieldBlur, onFieldChange, enableReinitialize, isDirty, defaultValues, ...rest } = props;
    const result = useReactHookForm<TValues>({ ...rest, defaultValues, mode: 'all' });
    const { trigger, getValues, handleSubmit } = result;

    useEffect(() => {
        // trigger once validation
        trigger();
    }, [trigger]);

    const prevDefaultValues = useRef<any>(defaultValues);
    const setValue = result.setValue;
    useEffect(() => {
        // Reinitialize & validate if any changes from default values.
        // TODO Investigate more react - hook - form, maybe such functionality already exist.
        if (enableReinitialize && defaultValues) {
            let hasChanges = false;
            Object.keys(defaultValues).forEach((key) => {
                const prevValue: any = prevDefaultValues.current[key];
                const newValue: any = (defaultValues as IDictionary<any>)[key];
                if (prevValue?.getTime && newValue?.getTime) {
                    if (Math.abs(dayjs(prevValue).diff(newValue, 'minutes')) > 0) {
                        hasChanges = true;
                        prevDefaultValues.current[key] = newValue;
                        setValue(key as Path<TValues>, newValue);
                    }
                } else if (JSON.stringify(prevValue || 0) !== JSON.stringify(newValue || 0)) {
                    hasChanges = true;
                    prevDefaultValues.current[key] = newValue;
                    setValue(key as Path<TValues>, newValue);
                }
            });
            if (hasChanges) {
                trigger();
            }
        }
    }, [defaultValues, enableReinitialize, setValue, trigger]);

    const setBlur = useCallback(
        (name) => {
            const handler: React.ChangeEventHandler<unknown> = _onBlurHandlersRef?.current[name as unknown as string];
            if (handler) {
                handler({
                    type: 'blur',
                    target: { name, value: getValues()[name as string] },
                } as React.ChangeEvent<any>);
            }
        },
        [getValues],
    );

    const customSetValue = useCallback(
        (name: any, value: any) => {
            setValue(name, value);
        },
        [setValue],
    );

    const submit = useCallback(() => {
        handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    const setBlurForAllFields = useCallback(() => {
        const keys = Object.keys(_onBlurHandlersRef.current);
        keys.forEach((key) => {
            setBlur(key);
        });
    }, [setBlur]);

    useEffect(() => {
        if (isDirty) {
            setBlurForAllFields();
        }
    }, [isDirty, setBlurForAllFields]);

    // Warning don't move to useMemo - errors field have same ref!!!
    const isValid = Object.keys(result.formState.errors).length === 0;

    return useMemo(() => {
        return {
            setBlur,
            setValue: customSetValue,
            setBlurForAllFields,
            submit,
            isValid,
            formContext: result,
            defaultValues: prevDefaultValues.current,
            props: {
                onSubmit: props.onSubmit,
                onFieldBlur: props.onFieldBlur,
                onFieldChange: props.onFieldChange,
            },
            _onBlurHandlers: _onBlurHandlersRef.current,
        };
    }, [
        customSetValue,
        isValid,
        props.onFieldBlur,
        props.onFieldChange,
        props.onSubmit,
        result,
        setBlur,
        setBlurForAllFields,
        submit,
    ]);
};
